import {useField, useFormikContext} from 'formik';
import React, {ReactElement, ReactNode} from 'react';
import InputMask, {InputState, MaskOptions} from 'react-input-mask';
import {isFieldRequired, useValidationSchema} from '../form/ValidationService';
import {NxFormikInputProps} from '../nxInput/NxFormikInput';
import NxInput from '../nxInput/NxInput';

export interface NxMaskedInputEvent {
  newValue: string;
  oldValue: string;
  permanents: number[];
  userInput: string;
}

export type NxFormikMaskedInputProps = Omit<NxFormikInputProps, 'type'> & {
  mask: string;
  maskChar?: string;
  placeholder?: string;
  alwaysShowMask?: boolean;
  beforeMaskedValueChange?(event: NxMaskedInputEvent): string;
}

const NxFormikMaskedInput = (props: NxFormikMaskedInputProps): ReactElement => {
  const context = useFormikContext();
  const [field, meta] = useField(props);
  const validationSchema = useValidationSchema();
  const required = props.required || isFieldRequired(validationSchema, field.name);
  const disabled = props.disabled || context.isSubmitting;

  /**
   * InputMask passes down every prop except the following ones:
   * onChange, onPaste, onMouseDown, onFocus, onBlur, value, disabled, readOnly.
   */
  const maskFunction = props.beforeMaskedValueChange ? getMaskedFunction(props.beforeMaskedValueChange) : undefined;
  return (
    <InputMask {...props} {...field} disabled={disabled}
               mask={props.mask ?? ''}
               maskChar={props.maskChar ?? ''}
               placeholder={props.placeholder}
               alwaysShowMask={props.alwaysShowMask}
               beforeMaskedValueChange={maskFunction}>
      {
        (inputProps: NxFormikInputProps): ReactNode =>
          <NxInput {...inputProps} error={meta.error} disabled={disabled} required={required}/>
      }
    </InputMask>
  );
};

const getMaskedFunction = (maskFunction: (event: NxMaskedInputEvent) => string) => {
  return (
    newState: InputState,
    oldState: InputState,
    userInput: string,
    maskOptions: MaskOptions
  ): InputState => {
    const newValue = maskFunction({
      newValue: newState.value,
      oldValue: oldState.value,
      permanents: maskOptions.permanents,
      userInput: userInput
    });
    return {
      value: newValue,
      selection: newState.selection
    };
  };
};

export default NxFormikMaskedInput;